/**
 * `useCardDescriptionDrop` — the card-Description OS-file drop behaviour,
 * shared by the create modal and the detail modal (view + edit).
 *
 * Dropping a file onto the description block ATTACHES it to the card
 * (via the FU-D presigned-URL flow). While the editor is editable it ALSO
 * embeds inline, routed BY MIME: an image becomes an image, a browser-
 * playable video (mp4 / webm / mov) becomes an inline player, and
 * everything else — including every other `video/*`, which the page
 * cannot play — becomes a file chip. The embed appears IMMEDIATELY at the
 * caret as a loading placeholder (the image carries a local `blob:`
 * preview; the other two a filename + spinner) that resolves to the
 * durable attachment-backed node once the upload lands. An in-body
 * affordance REPLACES the bottom pending chip (no double-show); a drop in
 * view mode, where the editor isn't editable, keeps using the pending
 * chip. Every path runs off a SINGLE upload per file — the image path
 * reuses `useImageUpload` (which returns the `attachmentId` for tracking
 * and a signed render URL), the video + file paths use `useFileUpload`.
 *
 * AN EMBED WHOSE UPLOAD FAILS BECOMES AN IN-PLACE ERROR STATE, NOT A
 * SILENT REMOVAL — the same contract the comment composers get from
 * `useInlineAttachmentUploads`. The node flips to `uploadingFailed` (the
 * NodeView renders "Upload failed" + Retry / Dismiss), the `File` is
 * retained in a per-instance map keyed by the node's placeholder id, and
 * the image's blob URL is kept alive so the error state still has a
 * preview to dim. Retry re-runs the network leg against the SAME node —
 * no new node, no new placeholder id — and stays cancellable; Dismiss
 * removes the node and frees both. A vanished placeholder reads as "the
 * drop never registered", which is what an offline drop used to leave
 * behind. Surfaces that don't pass the `mark*PlaceholderFailed`
 * callbacks keep the old remove-on-failure behaviour.
 *
 * Headless: pairs `useOsFileDrop` (drag state) with a transient pending
 * list the caller renders via `<PendingAttachmentStrip>`. Rows show
 * while in-flight / errored; a settled ('done') row lingers ~1.2s then
 * self-removes (the file already lives in the Attachments section). Errored rows
 * stay until the user dismisses them. Object URLs for image previews are
 * revoked on removal + unmount.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/api/client'
import { useToast } from '@/components/ToastProvider'
import {
  CARD_EMBED_CANCEL_UPLOAD_EVENT,
  CARD_EMBED_DISMISS_UPLOAD_EVENT,
  CARD_EMBED_RETRY_UPLOAD_EVENT,
  type CardEmbedUploadActionDetail,
} from '@/editor/nodes/card-embed-upload-events'
import {
  storageUploadErrorCode,
  useUploadFailureToast,
} from '@/features/uploads/upload-failure-toast'
import type { PendingUploadItem } from './PendingAttachmentStrip'
import {
  isAllowedAttachmentMime,
  maxBytesForAttachment,
  sizeKindFor,
} from './card-attachment-constraints'
import { trackPlaceholderProgress } from './placeholder-progress'
import { type PutProgress, isAbortError } from './put-with-progress'
import type { ParentKind } from './types'
import { throttleProgress } from './upload-registry'
import { useFileUpload } from './use-file-upload'
import { useImageUpload } from './use-image-upload'
import { useOsFileDrop } from './use-os-file-drop'
import { isVideoEmbedMime } from './video-embed-mime'

/** How long a settled ('done') chip lingers before it self-removes. */
const DONE_LINGER_MS = 1200

interface PendingRow extends PendingUploadItem {
  attachmentId?: string
}

/** What a Retry needs: the bytes, and which node kind holds them. */
interface RetainedEmbedUpload {
  kind: 'image' | 'file' | 'video'
  file: File
}

export interface UseCardDescriptionDropOptions {
  parentKind: ParentKind
  /** Omit / null for the create flow (two-phase attach on submit). */
  parentId?: string | null
  /** When false, the drag handlers pass events through untouched. */
  enabled?: boolean
  /**
   * Insert an in-body image placeholder at the caret showing the local
   * `blob:` preview + spinner. Return a placeholder id used to
   * resolve / fail the node once the upload settles, or `null` when the
   * editor isn't mounted / editable — in which case the drop falls back
   * to the attach-only pending chip. Omit on surfaces that never embed.
   */
  insertImagePlaceholder?: (blobUrl: string) => string | null
  /**
   * Swap an in-body placeholder for its resolved attachment (durable
   * `attachmentId` + fresh signed `src`) once the upload lands.
   */
  resolveImagePlaceholder?: (
    placeholderId: string,
    result: { attachmentId: string; src: string; alt: string },
  ) => void
  /** Remove an in-body image placeholder (Dismiss, cancel, fallback). */
  failImagePlaceholder?: (placeholderId: string) => void
  /**
   * Flip an in-body image placeholder into (`true`) or out of (`false`)
   * its error state. Supplying this is what turns a failed upload into a
   * Retry / Dismiss affordance instead of a silent removal; omit it and
   * the hook falls back to `failImagePlaceholder`.
   */
  markImagePlaceholderFailed?: (placeholderId: string, failed: boolean) => void
  /**
   * Insert an inline file chip (non-image) at the caret showing the
   * filename + spinner. Return a placeholder id, or `null` when the editor
   * isn't mounted / editable — in which case the drop falls back to the
   * attach-only pending chip. Omit on surfaces that never embed files.
   */
  insertFilePlaceholder?: (meta: {
    fileName: string
    sizeBytes: number
    mimeType: string
  }) => string | null
  /** Swap an in-body file placeholder for its resolved attachment id. */
  resolveFilePlaceholder?: (placeholderId: string, result: { attachmentId: string }) => void
  /** Remove an in-body file placeholder (Dismiss, cancel, fallback). */
  failFilePlaceholder?: (placeholderId: string) => void
  /** The file-chip counterpart of `markImagePlaceholderFailed`. */
  markFilePlaceholderFailed?: (placeholderId: string, failed: boolean) => void
  /**
   * Insert an inline video player placeholder at the caret showing the
   * filename + spinner. Return a placeholder id, or `null` when the editor
   * isn't mounted / editable — in which case the drop falls through to the
   * file chip, and then to the attach-only pending chip. Omit on surfaces
   * that render a playable video as a chip.
   */
  insertVideoPlaceholder?: (meta: { fileName: string; mimeType: string }) => string | null
  /** Swap an in-body video placeholder for its resolved attachment id. */
  resolveVideoPlaceholder?: (
    placeholderId: string,
    result: { attachmentId: string; mimeType: string },
  ) => void
  /** Remove an in-body video placeholder (Dismiss, cancel, fallback). */
  failVideoPlaceholder?: (placeholderId: string) => void
  /** The video counterpart of `markImagePlaceholderFailed`. */
  markVideoPlaceholderFailed?: (placeholderId: string, failed: boolean) => void
  /** Fired once per successful upload with its attachment id. */
  onUploaded?: (attachmentId: string) => void
  /**
   * Fired as soon as the server has minted an attachment row for an
   * upload — before its bytes land, and for an upload that later fails.
   * A two-phase surface uses it to know every row it may have to release
   * when the draft is abandoned.
   */
  onUploadStarted?: (attachmentId: string) => void
  /**
   * Take over what a DROP does. Without it a drop goes straight through
   * `acceptFiles`; a surface that must first prepare the editor (open it
   * for editing, place the caret where the files were let go) receives
   * the files and the drop point here and calls `acceptFiles` itself.
   */
  onDropFiles?: (files: File[], point: { x: number; y: number }) => void
}

export interface UseCardDescriptionDropResult {
  isDragActive: boolean
  dropHandlers: ReturnType<typeof useOsFileDrop>['dropHandlers']
  pendingItems: PendingUploadItem[]
  removePending: (localId: string) => void
  /** Clear every row + revoke object URLs (call on modal close / reset). */
  reset: () => void
  /**
   * Route files picked by a BUTTON through the exact path a drop takes
   * (validation, toasts, inline placeholder, upload, refetch). The toolbar's
   * Attach item calls this so click-to-attach and drag-to-attach can't drift.
   */
  acceptFiles: (files: File[]) => void
  /** How many uploads are still transferring. */
  inFlightCount: number
  /**
   * Abort every transfer still running. The surface calls it when the
   * draft that would have held the files is abandoned, so bytes stop
   * moving for a file nobody will keep.
   */
  cancelInFlight: () => void
}

function makeLocalId(file: File): string {
  return `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Release an attachment whose upload landed but whose row the user has
 * already dismissed. Best-effort — the 24h orphan sweep is the backstop,
 * and a failure here must never surface to someone who already said no.
 */
const deleteAttachment = (attachmentId: string): void => {
  void apiClient(`/api/uploads/${attachmentId}`, { method: 'DELETE' }).catch(() => {})
}

export function useCardDescriptionDrop({
  parentKind,
  parentId,
  enabled = true,
  insertImagePlaceholder,
  resolveImagePlaceholder,
  failImagePlaceholder,
  markImagePlaceholderFailed,
  insertFilePlaceholder,
  resolveFilePlaceholder,
  failFilePlaceholder,
  markFilePlaceholderFailed,
  insertVideoPlaceholder,
  resolveVideoPlaceholder,
  failVideoPlaceholder,
  markVideoPlaceholderFailed,
  onUploaded,
  onUploadStarted,
  onDropFiles,
}: UseCardDescriptionDropOptions): UseCardDescriptionDropResult {
  const { t } = useTranslation('uploads')
  const toast = useToast()
  const reportUploadFailure = useUploadFailureToast()
  const { uploadImage } = useImageUpload({ parentKind, parentId })
  const { uploadFile } = useFileUpload({ parentKind, parentId })

  const [pending, setPending] = useState<PendingRow[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  /** One controller per pending row, so its X can actually stop it. */
  const abortersRef = useRef<Map<string, AbortController>>(new Map())

  // Counted per attempt (a Retry is a new attempt), so a surface can hold
  // its Save while bytes are still moving.
  const [inFlightCount, setInFlightCount] = useState(0)
  const beginFlight = useCallback(() => setInFlightCount((n) => n + 1), [])
  const endFlight = useCallback(() => setInFlightCount((n) => Math.max(0, n - 1)), [])

  /**
   * Live preview blob URLs for in-body image embeds, keyed by the node's
   * placeholder id. Ownership lives here rather than in the upload's
   * closure because the URL now outlives a single attempt: a failed node
   * keeps its preview until Dismiss or a successful Retry, and the window
   * listeners have no way into that closure.
   */
  const embedBlobUrlsRef = useRef<Map<string, string>>(new Map())
  /**
   * Failed embeds, keyed by the node's placeholder id. Per instance — two
   * composers on one page never see each other's entries, which is what
   * lets the shared NodeView events be claimed by exactly one of them.
   */
  const failedEmbedsRef = useRef<Map<string, RetainedEmbedUpload>>(new Map())

  const revokeEmbedBlob = useCallback((placeholderId: string) => {
    const url = embedBlobUrlsRef.current.get(placeholderId)
    if (!url) return
    URL.revokeObjectURL(url)
    embedBlobUrlsRef.current.delete(placeholderId)
  }, [])

  const clearTimer = useCallback((localId: string) => {
    const timer = timersRef.current.get(localId)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(localId)
    }
  }, [])

  const removePending = useCallback(
    (localId: string) => {
      clearTimer(localId)
      // Stop the bytes AND release what already landed. Dropping the row
      // alone is what made this control a lie: the chip vanished, the
      // upload carried on, and the file was charged to the tenant.
      abortersRef.current.get(localId)?.abort()
      abortersRef.current.delete(localId)
      setPending((prev) => {
        const row = prev.find((r) => r.localId === localId)
        if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl)
        if (row?.status === 'done' && row.attachmentId) void deleteAttachment(row.attachmentId)
        return prev.filter((r) => r.localId !== localId)
      })
    },
    [clearTimer],
  )

  /**
   * Clear a finished row's chip WITHOUT touching its attachment. The
   * linger after a successful upload is only about tidying the progress
   * strip — the file was kept, so it must never go through
   * `removePending`, whose job is to release a file the user cancelled.
   */
  const dismissDoneRow = useCallback(
    (localId: string) => {
      clearTimer(localId)
      abortersRef.current.delete(localId)
      setPending((prev) => {
        const row = prev.find((r) => r.localId === localId)
        if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl)
        return prev.filter((r) => r.localId !== localId)
      })
    },
    [clearTimer],
  )

  const scheduleRemove = useCallback(
    (localId: string) => {
      clearTimer(localId)
      const timer = setTimeout(() => dismissDoneRow(localId), DONE_LINGER_MS)
      timersRef.current.set(localId, timer)
    },
    [clearTimer, dismissDoneRow],
  )

  const setStatus = useCallback(
    (localId: string, status: PendingUploadItem['status'], attachmentId?: string) => {
      setPending((prev) =>
        prev.map((r) =>
          r.localId === localId ? { ...r, status, ...(attachmentId ? { attachmentId } : {}) } : r,
        ),
      )
    },
    [],
  )

  // Kept separate from `setStatus` so a byte tick doesn't have to pretend
  // to be a status change. Throttled at the call site to whole percents.
  const setProgress = useCallback((localId: string, progress: PutProgress) => {
    setPending((prev) => prev.map((r) => (r.localId === localId ? { ...r, progress } : r)))
  }, [])

  // Attach-only path (the pending chip): non-image files, plus images
  // dropped while the editor isn't editable (view mode) — the image still
  // attaches and surfaces under Attachments, it just isn't embedded inline.
  const uploadOne = useCallback(
    async (localId: string, file: File, isImage: boolean) => {
      const onProgress = throttleProgress((progress) => setProgress(localId, progress))
      // The chip's X is LABELLED "Cancel upload", so it has to be one.
      // Without a controller here the transfer ran to completion after the
      // user dismissed the chip, the attachment was created, and its bytes
      // counted against the tenant's storage — for a file they had already
      // said they did not want.
      const controller = new AbortController()
      abortersRef.current.set(localId, controller)
      beginFlight()
      try {
        const options = {
          onProgress,
          signal: controller.signal,
          onPresigned: onUploadStarted,
        }
        const res = isImage ? await uploadImage(file, options) : await uploadFile(file, options)
        // Landed while the user was cancelling: the row is already gone,
        // so delete the attachment rather than leaving it charged.
        if (!abortersRef.current.has(localId)) {
          void deleteAttachment(res.attachmentId)
          return
        }
        setStatus(localId, 'done', res.attachmentId)
        onUploaded?.(res.attachmentId)
        scheduleRemove(localId)
      } catch (err) {
        if (isAbortError(err)) return
        // Per-file failure keeps the chip visible (error state) until the
        // user dismisses it; the presigned-PUT error is surfaced on the
        // chip, not as a toast, so a batch drop doesn't strobe toasts. A
        // storage refusal is the exception: the chip can't say WHY, and the
        // shared reporter folds a whole refused batch into one toast.
        setStatus(localId, 'error')
        if (storageUploadErrorCode(err)) reportUploadFailure(err, file.name)
      } finally {
        abortersRef.current.delete(localId)
        endFlight()
      }
    },
    [
      uploadImage,
      uploadFile,
      setStatus,
      setProgress,
      onUploaded,
      onUploadStarted,
      scheduleRemove,
      beginFlight,
      endFlight,
      reportUploadFailure,
    ],
  )

  // In-body embed path: the placeholder node is already showing the local
  // blob preview + spinner. Shared by the first attempt and every Retry —
  // that shared entry point is what makes a Retry re-target the SAME node,
  // since resolve / mark / remove all locate it by the placeholder id
  // fresh inside their own transaction. No pending chip for this file.
  const uploadEmbedded = useCallback(
    async (placeholderId: string, file: File) => {
      // The in-body node is the only surface for this file, so its
      // progress is published under the placeholder id the NodeView reads.
      const channel = trackPlaceholderProgress(placeholderId)
      // Keyed by PLACEHOLDER id, which is the only handle the in-body
      // node has — its cancel button names itself with it.
      const controller = new AbortController()
      abortersRef.current.set(placeholderId, controller)
      beginFlight()
      try {
        const res = await uploadImage(file, {
          onProgress: channel.onProgress,
          signal: controller.signal,
          onPresigned: onUploadStarted,
        })
        // Landed while the user was cancelling: the node is already gone,
        // so release the bytes rather than leaving them charged.
        if (!abortersRef.current.has(placeholderId)) {
          void deleteAttachment(res.attachmentId)
          revokeEmbedBlob(placeholderId)
          return
        }
        resolveImagePlaceholder?.(placeholderId, {
          attachmentId: res.attachmentId,
          src: res.url,
          alt: res.alt,
        })
        // The node renders the signed URL now; the preview bytes are done.
        revokeEmbedBlob(placeholderId)
        onUploaded?.(res.attachmentId)
      } catch (err) {
        // A cancel already removed the node and said what it did; an
        // "upload failed" toast on top of it reads as a second, unrelated
        // problem, and flipping the node to failed would resurrect it as
        // an error the user then has to dismiss.
        if (isAbortError(err)) return
        if (markImagePlaceholderFailed) {
          // Keep the node in the body as an ERROR state (Retry / Dismiss).
          // The blob URL deliberately survives — the failed NodeView dims
          // that preview, and revoking it here would leave a broken image.
          markImagePlaceholderFailed(placeholderId, true)
          failedEmbedsRef.current.set(placeholderId, { kind: 'image', file })
        } else {
          failImagePlaceholder?.(placeholderId)
          revokeEmbedBlob(placeholderId)
        }
        reportUploadFailure(err, file.name)
      } finally {
        abortersRef.current.delete(placeholderId)
        // Every exit, or the registry entry outlives the upload and the
        // unload guard nags forever. A Retry opens a fresh channel.
        channel.finish()
        endFlight()
      }
    },
    [
      uploadImage,
      resolveImagePlaceholder,
      failImagePlaceholder,
      markImagePlaceholderFailed,
      revokeEmbedBlob,
      onUploaded,
      onUploadStarted,
      beginFlight,
      endFlight,
      reportUploadFailure,
    ],
  )

  // In-body file-embed path: the placeholder chip is already showing the
  // filename + spinner. Same shared-entry-point contract as the image
  // path above; the chip carries no preview, so nothing to revoke.
  const uploadEmbeddedFile = useCallback(
    async (placeholderId: string, file: File) => {
      const channel = trackPlaceholderProgress(placeholderId)
      const controller = new AbortController()
      abortersRef.current.set(placeholderId, controller)
      beginFlight()
      try {
        const res = await uploadFile(file, {
          onProgress: channel.onProgress,
          signal: controller.signal,
          onPresigned: onUploadStarted,
        })
        if (!abortersRef.current.has(placeholderId)) {
          void deleteAttachment(res.attachmentId)
          return
        }
        resolveFilePlaceholder?.(placeholderId, { attachmentId: res.attachmentId })
        onUploaded?.(res.attachmentId)
      } catch (err) {
        if (isAbortError(err)) return
        if (markFilePlaceholderFailed) {
          markFilePlaceholderFailed(placeholderId, true)
          failedEmbedsRef.current.set(placeholderId, { kind: 'file', file })
        } else {
          failFilePlaceholder?.(placeholderId)
        }
        reportUploadFailure(err, file.name)
      } finally {
        abortersRef.current.delete(placeholderId)
        channel.finish()
        endFlight()
      }
    },
    [
      uploadFile,
      resolveFilePlaceholder,
      failFilePlaceholder,
      markFilePlaceholderFailed,
      onUploaded,
      onUploadStarted,
      beginFlight,
      endFlight,
      reportUploadFailure,
    ],
  )

  // In-body video-embed path: the placeholder player is already showing
  // the filename + spinner. A clone of the file path rather than a branch
  // inside it because the two resolve DIFFERENT node types through
  // different callbacks; the transfer itself is identical (`useFileUpload`
  // — a video is an attachment like any other, only its render differs).
  const uploadEmbeddedVideo = useCallback(
    async (placeholderId: string, file: File) => {
      const channel = trackPlaceholderProgress(placeholderId)
      const controller = new AbortController()
      abortersRef.current.set(placeholderId, controller)
      beginFlight()
      try {
        const res = await uploadFile(file, {
          onProgress: channel.onProgress,
          signal: controller.signal,
          onPresigned: onUploadStarted,
        })
        if (!abortersRef.current.has(placeholderId)) {
          void deleteAttachment(res.attachmentId)
          return
        }
        resolveVideoPlaceholder?.(placeholderId, {
          attachmentId: res.attachmentId,
          // The server's content type wins: a file picked with no MIME
          // (or a stale one) would otherwise teach the node a type the
          // stored bytes don't have, and the player keys its fallback off it.
          mimeType: res.contentType ?? file.type,
        })
        onUploaded?.(res.attachmentId)
      } catch (err) {
        if (isAbortError(err)) return
        if (markVideoPlaceholderFailed) {
          markVideoPlaceholderFailed(placeholderId, true)
          failedEmbedsRef.current.set(placeholderId, { kind: 'video', file })
        } else {
          failVideoPlaceholder?.(placeholderId)
        }
        reportUploadFailure(err, file.name)
      } finally {
        abortersRef.current.delete(placeholderId)
        channel.finish()
        endFlight()
      }
    },
    [
      uploadFile,
      resolveVideoPlaceholder,
      failVideoPlaceholder,
      markVideoPlaceholderFailed,
      onUploaded,
      onUploadStarted,
      beginFlight,
      endFlight,
      reportUploadFailure,
    ],
  )

  // Cancel / Retry / Dismiss from the in-body NodeViews. They have no
  // channel back to this hook — the card embed extensions are built once
  // per editor surface — so the buttons dispatch window events. The SAME
  // NodeViews serve the comment composers, so every listener here looks
  // the placeholder id up in its OWN maps and ignores ids it doesn't
  // hold; acting on every event would fight the other composer.
  useEffect(() => {
    const onCancel = (event: Event) => {
      const detail = (event as CustomEvent<CardEmbedUploadActionDetail>).detail
      if (!detail) return
      const controller = abortersRef.current.get(detail.placeholderId)
      if (!controller) return
      abortersRef.current.delete(detail.placeholderId)
      controller.abort()
      // Aborting alone would leave the node behind, so remove it here.
      // The kind isn't known at this point; each helper no-ops when the
      // placeholder id doesn't match a node of its own type.
      failImagePlaceholder?.(detail.placeholderId)
      failFilePlaceholder?.(detail.placeholderId)
      failVideoPlaceholder?.(detail.placeholderId)
      revokeEmbedBlob(detail.placeholderId)
    }
    const onRetry = (event: Event) => {
      const detail = (event as CustomEvent<CardEmbedUploadActionDetail>).detail
      if (!detail) return
      const retained = failedEmbedsRef.current.get(detail.placeholderId)
      if (!retained) return
      // Consumed up front so a double-click can't start two uploads.
      failedEmbedsRef.current.delete(detail.placeholderId)
      if (retained.kind === 'image') {
        markImagePlaceholderFailed?.(detail.placeholderId, false)
        void uploadEmbedded(detail.placeholderId, retained.file)
      } else if (retained.kind === 'video') {
        markVideoPlaceholderFailed?.(detail.placeholderId, false)
        void uploadEmbeddedVideo(detail.placeholderId, retained.file)
      } else {
        markFilePlaceholderFailed?.(detail.placeholderId, false)
        void uploadEmbeddedFile(detail.placeholderId, retained.file)
      }
    }
    const onDismiss = (event: Event) => {
      const detail = (event as CustomEvent<CardEmbedUploadActionDetail>).detail
      if (!detail) return
      const retained = failedEmbedsRef.current.get(detail.placeholderId)
      if (!retained) return
      failedEmbedsRef.current.delete(detail.placeholderId)
      if (retained.kind === 'image') {
        failImagePlaceholder?.(detail.placeholderId)
      } else if (retained.kind === 'video') {
        failVideoPlaceholder?.(detail.placeholderId)
      } else {
        failFilePlaceholder?.(detail.placeholderId)
      }
      revokeEmbedBlob(detail.placeholderId)
    }

    window.addEventListener(CARD_EMBED_CANCEL_UPLOAD_EVENT, onCancel)
    window.addEventListener(CARD_EMBED_RETRY_UPLOAD_EVENT, onRetry)
    window.addEventListener(CARD_EMBED_DISMISS_UPLOAD_EVENT, onDismiss)
    return () => {
      window.removeEventListener(CARD_EMBED_CANCEL_UPLOAD_EVENT, onCancel)
      window.removeEventListener(CARD_EMBED_RETRY_UPLOAD_EVENT, onRetry)
      window.removeEventListener(CARD_EMBED_DISMISS_UPLOAD_EVENT, onDismiss)
    }
  }, [
    failImagePlaceholder,
    failFilePlaceholder,
    failVideoPlaceholder,
    markImagePlaceholderFailed,
    markFilePlaceholderFailed,
    markVideoPlaceholderFailed,
    revokeEmbedBlob,
    uploadEmbedded,
    uploadEmbeddedFile,
    uploadEmbeddedVideo,
  ])

  const handleFiles = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const mime = file.type || 'application/octet-stream'
        if (!isAllowedAttachmentMime(mime)) {
          toast.error(t('unsupportedType', { filename: file.name }))
          continue
        }
        if (file.size > maxBytesForAttachment(mime)) {
          toast.error(t('tooLarge', { filename: file.name, kind: sizeKindFor(mime) }))
          continue
        }
        const isImage = mime.startsWith('image/')

        // Try the in-body embed first: insert a placeholder at the caret
        // (image → blob preview + spinner; playable video → player;
        // everything else → file chip). When the editor isn't mounted /
        // editable (the insert callback returns null), fall through to the
        // attach-only pending chip.
        if (isImage && insertImagePlaceholder) {
          const blobUrl = URL.createObjectURL(file)
          const placeholderId = insertImagePlaceholder(blobUrl)
          if (placeholderId) {
            embedBlobUrlsRef.current.set(placeholderId, blobUrl)
            void uploadEmbedded(placeholderId, file)
            continue
          }
          URL.revokeObjectURL(blobUrl)
        }
        // A playable video embeds as a PLAYER; every other non-image
        // (mkv, avi, a zip) stays a chip, which is the honest render for
        // bytes the page cannot play. Falls through to the chip when the
        // surface passed no video callbacks.
        if (!isImage && isVideoEmbedMime(mime) && insertVideoPlaceholder) {
          const placeholderId = insertVideoPlaceholder({
            fileName: file.name,
            mimeType: mime,
          })
          if (placeholderId) {
            void uploadEmbeddedVideo(placeholderId, file)
            continue
          }
        }
        if (!isImage && insertFilePlaceholder) {
          const placeholderId = insertFilePlaceholder({
            fileName: file.name,
            sizeBytes: file.size,
            mimeType: mime,
          })
          if (placeholderId) {
            void uploadEmbeddedFile(placeholderId, file)
            continue
          }
        }

        const localId = makeLocalId(file)
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined
        setPending((prev) => [
          ...prev,
          {
            localId,
            filename: file.name,
            contentType: mime,
            sizeBytes: file.size,
            status: 'uploading',
            previewUrl,
          },
        ])
        void uploadOne(localId, file, isImage)
      }
    },
    [
      toast,
      t,
      uploadOne,
      uploadEmbedded,
      uploadEmbeddedFile,
      uploadEmbeddedVideo,
      insertImagePlaceholder,
      insertFilePlaceholder,
      insertVideoPlaceholder,
    ],
  )

  const handleDrop = useCallback(
    (files: File[], point: { x: number; y: number }) => {
      if (onDropFiles) onDropFiles(files, point)
      else handleFiles(files)
    },
    [onDropFiles, handleFiles],
  )

  const { isDragActive, dropHandlers } = useOsFileDrop({
    enabled,
    onFilesDropped: handleDrop,
    onDirectoryRejected: () => toast.error(t('directoryRejected')),
  })

  const reset = useCallback(() => {
    for (const timer of timersRef.current.values()) clearTimeout(timer)
    timersRef.current.clear()
    // The editor (and every node in it) goes away with the surface, so a
    // retained failed embed has nothing left to act on — free its bytes.
    for (const url of embedBlobUrlsRef.current.values()) URL.revokeObjectURL(url)
    embedBlobUrlsRef.current.clear()
    failedEmbedsRef.current.clear()
    setPending((prev) => {
      for (const row of prev) {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      }
      return []
    })
  }, [])

  // Revoke any outstanding object URLs + timers on unmount.
  useEffect(() => reset, [reset])

  const cancelInFlight = useCallback(() => {
    const controllers = [...abortersRef.current.values()]
    abortersRef.current.clear()
    for (const controller of controllers) controller.abort()
  }, [])

  return {
    isDragActive,
    dropHandlers,
    pendingItems: pending,
    removePending,
    reset,
    acceptFiles: handleFiles,
    inFlightCount,
    cancelInFlight,
  }
}
